import { TsRestClient } from '@eluve/api-client-provider';
import { CorrelatedLogger } from '@eluve/logger';
import { getMetadataFromFileName } from '@eluve/utils';

import { DexieDb } from './files-db';
import {
  isSlotResolvedAgainstGcs,
  resolveReservedUploadAgainstGcs,
} from './resolveReservedUploadAgainstGcs';
import { UserFileSystem } from './user-file-system.store';

type ReconcileOrphanCandidatesArgs = {
  apiClient: TsRestClient;
  db: DexieDb;
  userFileSystem: UserFileSystem;
  log: CorrelatedLogger;
  tenantId: string;
  userId: string;
  deviceId: string;
};

type Candidate = {
  fileId: string;
  appointmentId: string;
  fileName: string;
  matchedByDeviceId: boolean;
};

/**
 * Per-candidate decision outcomes. Each tick aggregates these into a summary
 * log so Datadog can split by reason. Names are stable — they're the canonical
 * vocabulary for the orphan-reconciliation observability story. [ENG-4024]
 *
 * `cancelled_device_matched_no_local_row` was added for [ENG-4180]: when the
 * server confirms via device_id that a row belongs to this client but the
 * client has no local Dexie row, we now cancel authoritatively instead of
 * deferring.
 *
 * `resolved_against_gcs` was added for [ENG-4562]: before any cancel, the
 * slot is reconciled against GCS truth (resolveReservedUpload) — when the
 * backend resolves it from stored bytes, the cancel is skipped entirely.
 */
type ReconcileOutcome =
  | 'cancelled'
  | 'cancelled_device_matched_no_local_row'
  | 'resolved_against_gcs'
  | 'deferred_bytes_present'
  | 'deferred_opfs_unavailable'
  | 'no_local_row'
  | 'file_id_mismatch'
  | 'opfs_probe_failed'
  | 'cancel_api_failed';

type ReconcileSingleCandidateArgs = Omit<
  ReconcileOrphanCandidatesArgs,
  'userId'
> & {
  candidate: Candidate;
  userId: string;
};

const reconcileSingleCandidate = async ({
  apiClient,
  db,
  userFileSystem,
  log,
  tenantId,
  userId,
  candidate,
}: ReconcileSingleCandidateArgs): Promise<ReconcileOutcome> => {
  const { fileId, appointmentId, fileName, matchedByDeviceId } = candidate;

  // Sanity check: fileId from the server must match what this device would
  // derive locally. The server already filters by user_id; this is defense in
  // depth against a future leak in that filter.
  const { databaseFileId } = getMetadataFromFileName({ fileName, userId });
  if (databaseFileId !== fileId) {
    log('warn', 'Orphan candidate skipped — fileId mismatch (defensive)', {
      fileId,
      fileName,
      appointmentId,
      derivedFileId: databaseFileId,
      matchedByDeviceId,
    });
    return 'file_id_mismatch';
  }

  const localRow = await db.appointmentFiles.get(fileName);
  if (!localRow) {
    if (matchedByDeviceId) {
      // Server confirms this row was created by *this* device's id, but the
      // local Dexie row is gone (partial wipe, IDB corruption, etc.). The
      // bytes can't exist locally without the index row — but they may have
      // finalized in GCS before the local state was lost, so reconcile
      // against GCS truth first. [ENG-4180][ENG-4562]
      const resolution = await resolveReservedUploadAgainstGcs({
        apiClient,
        log,
        tenantId,
        appointmentId,
        fileId,
        fileName,
      });
      if (isSlotResolvedAgainstGcs(resolution)) {
        log('info', 'Orphan slot resolved against GCS — skipping cancel', {
          fileId,
          fileName,
          appointmentId,
          resolution,
        });
        return 'resolved_against_gcs';
      }

      log(
        'info',
        'Orphan candidate device-matched with no local row — cancelling authoritatively',
        { fileId, fileName, appointmentId, resolution },
      );

      try {
        await apiClient.appointments.cancelOrphanedFile({
          params: { tenantId, appointmentId, fileId },
          body: { trigger: 'server_prompted' },
        });
      } catch (error) {
        log('warn', 'server_prompted cancel API call failed', {
          fileId,
          fileName,
          appointmentId,
          error,
          matchedByDeviceId,
        });
        return 'cancel_api_failed';
      }

      return 'cancelled_device_matched_no_local_row';
    }

    // Legacy fallback bucket (matchedByDeviceId === false): the row predates
    // device-id rollout, so we can't positively assert ownership. Preserve the
    // existing multi-device-safe behaviour — leave it alone and let the
    // originating device reconcile it. [ENG-4024]
    log(
      'info',
      'Orphan candidate skipped — no local Dexie row (legacy bucket)',
      {
        fileId,
        fileName,
        appointmentId,
      },
    );
    return 'no_local_row';
  }

  if (userFileSystem.type !== 'available') {
    // Can't verify OPFS state — defer to the next tick.
    log('info', 'Orphan candidate deferred — OPFS not available yet', {
      fileId,
      fileName,
      appointmentId,
    });
    return 'deferred_opfs_unavailable';
  }

  let bytesPresent = false;
  try {
    const appointmentDir =
      await userFileSystem.appointmentsDir.getDirectoryHandle(appointmentId);
    await appointmentDir.getFileHandle(fileName);
    bytesPresent = true;
  } catch (error) {
    if (error instanceof DOMException && error.name === 'NotFoundError') {
      bytesPresent = false;
    } else {
      // Unknown error — bail without acting on this candidate.
      log('warn', 'Failed to probe OPFS during orphan reconciliation', {
        fileName,
        appointmentId,
        error,
      });
      return 'opfs_probe_failed';
    }
  }

  if (bytesPresent) {
    // Surfacing this is useful: a candidate with present bytes means the regular
    // sync loop hasn't closed it out yet. Steady drumbeat here suggests the
    // sync loop is rate-limited or stuck for some other reason.
    log('info', 'Orphan candidate has bytes — deferring to regular sync', {
      fileId,
      fileName,
      appointmentId,
    });
    return 'deferred_bytes_present';
  }

  // Local bytes are gone, but the upload may have finalized in GCS before
  // the local state was lost — reconcile against GCS truth before
  // cancelling. [ENG-4562]
  const resolution = await resolveReservedUploadAgainstGcs({
    apiClient,
    log,
    tenantId,
    appointmentId,
    fileId,
    fileName,
  });
  if (isSlotResolvedAgainstGcs(resolution)) {
    log('info', 'Orphan slot resolved against GCS — skipping cancel', {
      fileId,
      fileName,
      appointmentId,
      resolution,
    });
    // The slot is settled server-side; tear down the local row so the next
    // sync stops retrying it.
    await db.appointmentFiles.delete(fileName).catch(() => undefined);
    return 'resolved_against_gcs';
  }

  log('info', 'Escalating orphan to backend cancel (server_prompted)', {
    fileId,
    fileName,
    appointmentId,
    resolution,
  });

  try {
    await apiClient.appointments.cancelOrphanedFile({
      params: { tenantId, appointmentId, fileId },
      body: { trigger: 'server_prompted' },
    });
  } catch (error) {
    log('warn', 'server_prompted cancel API call failed', {
      fileId,
      fileName,
      appointmentId,
      error,
    });
    return 'cancel_api_failed';
  }

  // Tear down the local row + OPFS entry so the next sync ignores it.
  await db.appointmentFiles.delete(fileName).catch(() => undefined);
  try {
    const appointmentDir =
      await userFileSystem.appointmentsDir.getDirectoryHandle(appointmentId);
    await appointmentDir.removeEntry(fileName);
  } catch {
    // OPFS already empty — fine.
  }

  return 'cancelled';
};

const buildEmptyTickSummary = (): Record<ReconcileOutcome, number> => ({
  cancelled: 0,
  cancelled_device_matched_no_local_row: 0,
  resolved_against_gcs: 0,
  deferred_bytes_present: 0,
  deferred_opfs_unavailable: 0,
  no_local_row: 0,
  file_id_mismatch: 0,
  opfs_probe_failed: 0,
  cancel_api_failed: 0,
});

/**
 * Per-tick server-driven orphan reconciliation. Asks the API for PENDING_UPLOAD
 * rows on COMPLETED appointments past the server-side age buffer, then for each
 * candidate decides via the deterministic file id (createStableUuid(userId-fileName)):
 *
 *  - Local Dexie row + OPFS bytes present → no-op (existing sync flow will pick it up).
 *  - Local Dexie row + OPFS NotFoundError  → POST cancel-orphaned (server_prompted).
 *  - No local Dexie row                    → no-op (file belongs to a different device).
 *
 * Multi-device safety: the deterministic file id ensures device B simply has no
 * matching Dexie row and exits via the no-op branch. [ENG-4024]
 */
export const reconcileOrphanCandidates = async (
  args: ReconcileOrphanCandidatesArgs,
): Promise<void> => {
  const { apiClient, db, userFileSystem, log, tenantId, userId, deviceId } =
    args;

  // Skip the API call entirely when OPFS isn't ready — every candidate would
  // exit as `deferred_opfs_unavailable` anyway, and the next reconciliation
  // tick will retry. Avoids a network round trip + per-candidate Dexie
  // lookups during the brief window before OPFS comes online.
  if (userFileSystem.type !== 'available') {
    log('info', 'Orphan reconciliation skipped — OPFS not available yet');
    return;
  }

  const response = await apiClient.appointments.listOrphanedPendingUploads({
    params: { tenantId },
    query: { deviceId },
  });

  if (response.status !== 200) {
    log('warn', 'orphan-candidate fetch returned non-200', {
      status: response.status,
    });
    return;
  }

  const candidates = response.body;
  if (!candidates.length) {
    return;
  }

  log('info', 'Reconciling orphan candidates', { count: candidates.length });

  // Per-candidate work is independent — OPFS probes and the cancel POST can
  // run in parallel. A single tick stays bounded since the server caps the
  // candidate list. [ENG-4024]
  const outcomes = await Promise.all(
    candidates.map((candidate) =>
      reconcileSingleCandidate({
        apiClient,
        db,
        userFileSystem,
        log,
        tenantId,
        userId,
        deviceId,
        candidate,
      }),
    ),
  );

  const summary = outcomes.reduce((acc, outcome) => {
    acc[outcome] += 1;
    return acc;
  }, buildEmptyTickSummary());

  // Single canonical per-tick summary — operators query this one log line to
  // see the shape of orphan reconciliation activity at a glance.
  log('info', 'Orphan reconciliation tick complete', {
    totalCandidates: candidates.length,
    ...summary,
  });
};
